import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import DateCurrentInput from '../inputs/DateCurrentInput';
import { RNMachineRepair, RNMachineRepairField } from '../types/rnMachineRepairs';

const inputClass = 'w-full h-6 px-1 border-0 outline-none text-[11px] bg-transparent hover:bg-blue-50 focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50';

// Одобрувањето е момент, не ден — истиот DD.MM.YYYY HH:mm што го користи Продуктивност.
const formatDateTime = (value?: string | null) => {
  if (!value) return '';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY HH:mm') : value;
};

interface Props {
  machineId: string;
  machineName: string;
  ruAssembly: string;
  ruHandling: string;
  repair?: RNMachineRepair;
  checked: boolean;
  onCheck: () => void;
  onChange: (field: RNMachineRepairField, value: string) => void;
  onApprove: (next: boolean) => void;
  approving: boolean;
  // Потписот важи само врз зачувана поправка: со незачувани измени копчето е заклучено,
  // инаку серверот би го поништил потписот при следното зачувување (GMP инвалидација).
  dirty: boolean;
  readOnly: boolean;
  canApprove: boolean;
  registerInputRef: (col: number, el: HTMLInputElement | null) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>, col: number) => void;
}

const RNSubprocessMachineRow: React.FC<Props> = ({
  machineName, ruAssembly, ruHandling, repair, checked, onCheck, onChange, onApprove,
  approving, dirty, readOnly, canApprove, registerInputRef, onKeyDown,
}) => {
  const { t } = useTranslation();
  const approved = !!repair?.approved;
  const hasContent = !!(repair?.repairText || repair?.repairDate || repair?.repairBy);

  // Едно копче ја носи целата состојба: потпишано, чека потпис, или ништо досега.
  const blocked = readOnly || !canApprove || approving || (dirty && !approved);
  const title = !canApprove
    ? t('rnMachineRepairs.noApprovePermission') || undefined
    : dirty && !approved
      ? t('rnMachineRepairs.saveBeforeApprove') || undefined
      : undefined;

  const stateClass = approved
    ? 'border-green-300 bg-green-50 text-green-800 hover:bg-green-100'
    : hasContent
      ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
      : 'border-gray-200 bg-white text-gray-500 hover:bg-blue-50';

  return (
    <tr
      onClick={onCheck}
      className={`cursor-pointer hover:bg-blue-50 focus-within:bg-blue-50 ${checked ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
    >
      {/* Checkbox — stop propagation so clicking the cell doesn't double-fire the row handler */}
      <td className="border border-gray-300 h-[30px] px-2 text-center" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onCheck()}
          className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500/50 cursor-pointer"
        />
      </td>

      {/* Машина + двете РУ: read-only, влечени од шифрарникот на машината. */}
      <td className="border border-gray-300 h-[30px] px-1.5 text-[11px] text-gray-700">{machineName}</td>
      <td className="border border-gray-300 h-[30px] px-1.5 text-[11px] text-gray-700">{ruAssembly}</td>
      <td className="border border-gray-300 h-[30px] px-1.5 text-[11px] text-gray-700">{ruHandling}</td>

      <td className="border border-gray-300 px-1.5 py-1 align-top" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          disabled={blocked}
          title={title}
          onClick={() => onApprove(!approved)}
          className={`w-full rounded-md border px-2 py-1 text-left text-[10px] leading-tight shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50 ${stateClass} ${blocked ? 'cursor-not-allowed opacity-60' : ''}`}
        >
          {approved ? (
            <>
              <span className="font-medium">✓ {t('rnMachineRepairs.approvedState')}</span>
              <span className="block text-green-700">{repair?.approvedByName || ''}</span>
              <span className="block text-green-700">{formatDateTime(repair?.approvedAt)}</span>
            </>
          ) : (
            <span className="font-medium">
              {hasContent ? t('rnMachineRepairs.pendingApproval') : t('rnMachineRepairs.approveAction')}
            </span>
          )}
        </button>
      </td>

      <td className="border border-gray-300 h-[30px] px-1" onClick={(e) => e.stopPropagation()}>
        <input
          ref={(el) => registerInputRef(0, el)}
          className={inputClass}
          value={repair?.repairText || ''}
          maxLength={500}
          disabled={readOnly}
          onChange={(e) => onChange('repairText', e.target.value)}
          onKeyDown={(e) => onKeyDown(e, 0)}
        />
      </td>
      <td className="border border-gray-300 h-[30px] px-1" onClick={(e) => e.stopPropagation()}>
        <DateCurrentInput clearable={false}
          value={repair?.repairDate || ''}
          disabled={readOnly}
          inputRef={(el) => registerInputRef(1, el)}
          inputClassName={inputClass}
          onChange={(value) => onChange('repairDate', value)}
          onKeyDown={(e) => onKeyDown(e as React.KeyboardEvent<HTMLInputElement>, 1)}
        />
      </td>
      <td className="border border-gray-300 h-[30px] px-1" onClick={(e) => e.stopPropagation()}>
        <input
          ref={(el) => registerInputRef(2, el)}
          className={inputClass}
          value={repair?.repairBy || ''}
          maxLength={100}
          disabled={readOnly}
          onChange={(e) => onChange('repairBy', e.target.value)}
          onKeyDown={(e) => onKeyDown(e, 2)}
        />
      </td>
    </tr>
  );
};

export default RNSubprocessMachineRow;
